import React, { useEffect } from 'react';
import { useNavigateWithSearch } from '../../../common/hooks/useNavigateWithSearch';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@mui/material';
import { PageTitle } from '../../../common/components/PageTitle';
import css from './BookingTransferForm.module.css';
import LeftLogo from '../../../assets/images/left_logo.svg?react';
import { AddressFields } from './AddressFields';
import { AIRPORT_MUC, AIRPORT_NUE } from './airports';
import { DetailFields } from './DetailFields';
import { HolidayBlockHint } from './HolidayBlockHint';
import { MUNICH_TRANSFERS, TRANSFER } from '../../models/transfer';
import { OrderActions } from './OrderActions';
import { PlacesService } from '../../../api/PlacesService';
import { proceedOrShowErrors } from '../../utils/validationUx';

const BookingTransferForm = ({
    url,
    data: { firstTransfer, secondTransfer },
    touch: { touchFirstTransfer, touchSecondTransfer },
    update: { updateFirstTransfer, updateSecondTransfer, addTransfer, removeTransfer },
    errorsSrc: { firstTransferErrorSrc, secondTransferErrorSrc },
    validationErrors,
    showValidationErrors,
    user,
}) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigateWithSearch();

    const isDetailPartAvailable = Boolean(firstTransfer.typeId);
    const isAddReturnTransferAvailable = Boolean(
        !secondTransfer &&
        [TRANSFER.TO_NUREMBERG, TRANSFER.FROM_NUREMBERG, TRANSFER.TO_MUNICH, TRANSFER.FROM_MUNICH].includes(
            firstTransfer.typeId,
        ),
    );
    const isRemoveReturnTransferAvailable = Boolean(secondTransfer);
    const disableSecondAddressFrom = Boolean(
        secondTransfer && [TRANSFER.FROM_MUNICH, TRANSFER.FROM_NUREMBERG].includes(secondTransfer.typeId),
    );
    const disableSecondAddressTo = Boolean(
        secondTransfer && [TRANSFER.TO_MUNICH, TRANSFER.TO_NUREMBERG].includes(secondTransfer.typeId),
    );

    const isMunichTransfer = MUNICH_TRANSFERS.includes(firstTransfer.typeId);

    const updateTransferTypes = () => {
        const addressFrom = firstTransfer.addressFrom;
        const addressTo = firstTransfer.addressTo;
        if (!addressFrom.place || !addressTo.place) {
            return;
        }

        if (addressFrom.id === AIRPORT_MUC) {
            updateFirstTransfer({ name: 'typeId', value: TRANSFER.FROM_MUNICH });
            return;
        }
        if (addressFrom.id === AIRPORT_NUE) {
            updateFirstTransfer({ name: 'typeId', value: TRANSFER.FROM_NUREMBERG });
            return;
        }
        if (addressTo.id === AIRPORT_MUC) {
            updateFirstTransfer({ name: 'typeId', value: TRANSFER.TO_MUNICH });
            return;
        }
        if (addressTo.id === AIRPORT_NUE) {
            updateFirstTransfer({ name: 'typeId', value: TRANSFER.TO_NUREMBERG });
            return;
        }
        updateFirstTransfer({ name: 'typeId', value: TRANSFER.SPECIAL });
    };

    useEffect(updateTransferTypes, [firstTransfer.addressFrom, firstTransfer.addressTo]);

    const onNext = (e) => {
        e.stopPropagation();
        e.preventDefault();

        proceedOrShowErrors({
            validationErrors,
            showValidationErrors,
            proceed: () => navigate(`${url}/category`),
        });
    };

    const isValid = !validationErrors;

    const [firstTransferError, secondTransferError] = [firstTransferErrorSrc, secondTransferErrorSrc].map(
        (transferError) => transferError.bind(null, validationErrors),
    );

    const nextButton = (
        <Button
            sx={{
                width: {
                    xs: '100%',
                    sm: 'auto',
                },
                mt: {
                    xs: 1.5,
                    sm: 0,
                },
                ml: {
                    sm: 1.5,
                },
            }}
            type="submit"
            variant="contained"
            color="secondary"
            size="large"
        >
            {t('button.next')}
        </Button>
    );

    return (
        <>
            <PageTitle>{t('main-form')}</PageTitle>
            <Box
                component="form"
                className={cn(css.box, secondTransfer ? css.twoTransfers : '')}
                sx={(theme) => theme.mixins.box(theme)}
                noValidate
                autoComplete="off"
                onSubmit={onNext}
                data-cy="booking-1"
            >
                <div className={css.logoBox}>
                    <LeftLogo className={css.logo} />
                    <Typography variant="h5" sx={{ mt: 2 }}>
                        {t('logo')}
                    </Typography>
                </div>
                <Box className={css.formBox}>
                    {secondTransfer && (
                        <Typography variant="h5" sx={{ mb: 2, pl: 5 }}>
                            {t('booking.transfer1')}
                        </Typography>
                    )}
                    <AddressFields
                        transfer={firstTransfer}
                        updateTransfer={updateFirstTransfer}
                        name="addressFrom"
                        touchTransfer={touchFirstTransfer}
                        transferError={firstTransferError}
                        user={user}
                        t={t}
                        search={PlacesService.search}
                        getPlace={PlacesService.getPlace}
                        searchPlaceId={PlacesService.searchPlaceId}
                        language={i18n.language}
                    />
                    <AddressFields
                        transfer={firstTransfer}
                        updateTransfer={updateFirstTransfer}
                        name="addressTo"
                        touchTransfer={touchFirstTransfer}
                        transferError={firstTransferError}
                        user={user}
                        t={t}
                        search={PlacesService.search}
                        getPlace={PlacesService.getPlace}
                        searchPlaceId={PlacesService.searchPlaceId}
                        language={i18n.language}
                    />
                    {isDetailPartAvailable && (
                        <>
                            <DetailFields
                                transfer={firstTransfer}
                                updateTransfer={updateFirstTransfer}
                                touchTransfer={touchFirstTransfer}
                                transferError={firstTransferError}
                            />
                            <HolidayBlockHint transfer={firstTransfer} />
                        </>
                    )}
                    {!isRemoveReturnTransferAvailable && (
                        <OrderActions
                            isBookingsValid={isValid}
                            firstTransfer={firstTransfer}
                            secondTransfer={secondTransfer}
                        >
                            <Box sx={{ mt: 3, textAlign: 'right' }}>
                                {isAddReturnTransferAvailable && (
                                    <Button
                                        color="inherit"
                                        sx={{
                                            position: 'relative',
                                            width: {
                                                xs: '100%',
                                                sm: 'auto',
                                            },
                                        }}
                                        type="button"
                                        variant="outlined"
                                        size="large"
                                        onClick={addTransfer}
                                    >
                                        + {t('button.addBackTransfer')}
                                        {isMunichTransfer && (
                                            <Typography
                                                variant="caption2"
                                                sx={{
                                                    position: 'absolute',
                                                    top: -12,
                                                    right: 8,
                                                    color: 'primary.contrastText',
                                                    backgroundColor: 'primary.main',
                                                    textTransform: 'none',
                                                    borderRadius: 1,
                                                    px: 0.75,
                                                }}
                                            >
                                                {t('saveNow')}
                                            </Typography>
                                        )}
                                    </Button>
                                )}
                                {nextButton}
                            </Box>
                        </OrderActions>
                    )}
                </Box>
                {secondTransfer && (
                    <Box sx={{ ml: 3 }} className={css.formBox}>
                        {secondTransfer && (
                            <Typography variant="h5" sx={{ mb: 2, pl: 5 }}>
                                {t('booking.transfer2')}
                            </Typography>
                        )}
                        <AddressFields
                            transfer={secondTransfer}
                            updateTransfer={updateSecondTransfer}
                            name="addressFrom"
                            touchTransfer={touchSecondTransfer}
                            transferError={secondTransferError}
                            disabled={disableSecondAddressFrom}
                            user={user}
                            t={t}
                            search={PlacesService.search}
                            getPlace={PlacesService.getPlace}
                            searchPlaceId={PlacesService.searchPlaceId}
                            language={i18n.language}
                        />
                        <AddressFields
                            transfer={secondTransfer}
                            updateTransfer={updateSecondTransfer}
                            name="addressTo"
                            touchTransfer={touchSecondTransfer}
                            transferError={secondTransferError}
                            disabled={disableSecondAddressTo}
                            user={user}
                            t={t}
                            search={PlacesService.search}
                            getPlace={PlacesService.getPlace}
                            searchPlaceId={PlacesService.searchPlaceId}
                            language={i18n.language}
                        />
                        {isDetailPartAvailable && (
                            <>
                                <DetailFields
                                    transfer={secondTransfer}
                                    updateTransfer={updateSecondTransfer}
                                    touchTransfer={touchSecondTransfer}
                                    transferError={secondTransferError}
                                />
                                <HolidayBlockHint transfer={secondTransfer} />
                            </>
                        )}
                        {isRemoveReturnTransferAvailable && (
                            <OrderActions
                                isBookingsValid={isValid}
                                firstTransfer={firstTransfer}
                                secondTransfer={secondTransfer}
                            >
                                <Box sx={{ mt: 3, textAlign: 'right' }}>
                                    <Button
                                        color="inherit"
                                        sx={{
                                            width: {
                                                xs: '100%',
                                                sm: 'auto',
                                            },
                                        }}
                                        type="button"
                                        variant="outlined"
                                        size="large"
                                        onClick={removeTransfer}
                                    >
                                        - {t('button.removeBackTransfer')}
                                    </Button>
                                    {nextButton}
                                </Box>
                            </OrderActions>
                        )}
                    </Box>
                )}
            </Box>
        </>
    );
};

export { BookingTransferForm };
